import { useState, useEffect } from 'react';
import { Language } from '../types';
import { TRANSLATIONS } from '../translations';
import { CONFIG } from '../config';

interface CookieConsentProps {
    language: Language;
}

export default function CookieConsent({ language }: CookieConsentProps) {
    const [show, setShow] = useState(false);
    const t = TRANSLATIONS[language];

    const applyConsentMode = (consent: 'accepted' | 'declined') => {
        const win = window as Window & {
            gtag?: (...args: unknown[]) => void;
            adsbygoogle?: { requestNonPersonalizedAds?: number };
        };

        if (typeof win.gtag === 'function') {
            if (consent === 'accepted') {
                win.gtag('consent', 'update', {
                    ad_storage: 'granted',
                    ad_user_data: 'granted',
                    ad_personalization: 'granted',
                    analytics_storage: 'denied'
                });
            } else {
                win.gtag('consent', 'update', {
                    ad_storage: 'denied',
                    ad_user_data: 'denied',
                    ad_personalization: 'denied',
                    analytics_storage: 'denied'
                });
                win.gtag('set', 'ads_data_redaction', true);
            }
        }

        win.adsbygoogle = win.adsbygoogle || {};
        win.adsbygoogle.requestNonPersonalizedAds = consent === 'declined' ? 1 : 0;
    };

    useEffect(() => {
        const consent = localStorage.getItem(CONFIG.STORAGE_KEYS.COOKIE_CONSENT);
        if (consent === 'accepted' || consent === 'declined') {
            applyConsentMode(consent);
            return;
        }

        if (!consent) {
            // Delay showing the consent banner
            const timer = setTimeout(() => setShow(true), 2000);
            return () => clearTimeout(timer);
        }
    }, []);

    const handleAccept = () => {
        localStorage.setItem(CONFIG.STORAGE_KEYS.COOKIE_CONSENT, 'accepted');
        applyConsentMode('accepted');
        setShow(false);
    };

    const handleDecline = () => {
        localStorage.setItem(CONFIG.STORAGE_KEYS.COOKIE_CONSENT, 'declined');
        applyConsentMode('declined');
        setShow(false);
    };

    if (!show) return null;

    return (
        <div className="fixed bottom-0 left-0 right-0 bg-[#0d0d0d]/95 backdrop-blur-md border-t border-[#4a7c59]/30 p-4 z-50 animate-fade-in-up">
            <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center gap-4">
                <div className="flex-1">
                    <h3 className="font-semibold text-white mb-1">{t.cookie_title}</h3>
                    <p className="text-sm text-[#888]">{t.cookie_message}</p>
                </div>
                <div className="flex gap-2">
                    <button
                        onClick={handleDecline}
                        className="px-4 py-2 border border-[#4a7c59]/50 text-[#888] hover:text-white rounded-lg transition-colors text-sm"
                    >
                        {t.cookie_decline}
                    </button>
                    <button
                        onClick={handleAccept}
                        className="px-4 py-2 bg-[#4a7c59] hover:bg-[#2d5a3c] text-white rounded-lg transition-colors text-sm"
                    >
                        {t.cookie_accept}
                    </button>
                </div>
            </div>
        </div>
    );
}
